import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dossiersAPI } from '../services/api';

// ════════════════════════════════════════════════════════════════════════════
// Lexback Sign — outil de signature électronique interne
// ════════════════════════════════════════════════════════════════════════════
// Alternative française à Yousign / PandaDoc / DocuSign, intégrée à Lexback :
// - Conforme eIDAS (signature électronique simple SES + avancée SEA)
// - Stockage souverain (Cloudflare R2 EU + horodatage qualifié)
// - Workflow natif Lexback : un dossier → un envoi → traçabilité Airtable
// ════════════════════════════════════════════════════════════════════════════

const STATUS_CONFIG = {
  brouillon:  { label: 'Brouillon',     color: '#8b8576', bg: 'rgba(139,133,118,0.10)' },
  envoye:     { label: 'Envoyé',        color: '#1A55A0', bg: 'rgba(26,85,160,0.08)' },
  partiel:    { label: 'Partiel',       color: '#d97706', bg: 'rgba(217,119,6,0.10)' },
  signe:      { label: 'Signé',         color: '#1A7A4A', bg: 'rgba(26,122,74,0.10)' },
  expire:     { label: 'Expiré',        color: '#B03020', bg: 'rgba(176,48,32,0.10)' },
  refuse:     { label: 'Refusé',        color: '#B03020', bg: 'rgba(176,48,32,0.10)' },
};

const TYPE_SIG = {
  ses: { label: 'Simple (SES)',   tooltip: 'Signature Électronique Simple — eIDAS, validité juridique standard' },
  sea: { label: 'Avancée (SEA)',  tooltip: 'Signature Électronique Avancée — identité vérifiée, valeur probante renforcée' },
};

const card = { background: '#fff', border: '0.5px solid rgba(0,0,0,0.07)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  } catch { return '—'; }
};

function Badge({ statut }) {
  const s = STATUS_CONFIG[statut] || STATUS_CONFIG.brouillon;
  return (
    <span style={{ fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '4px', color: s.color, background: s.bg }}>
      {s.label}
    </span>
  );
}

// ── Mock data : à brancher sur /api/signatures dès que le backend sera prêt ──
const MOCK_ENVOIS = [
  {
    id: 'sig_001',
    reference: 'SIG-2026-001',
    dossier_ref: 'AT-recABC123',
    societe_nom: 'AZAD (EX : SIVRI)',
    document_nom: 'PV AGE - cession parts.pdf',
    type: 'sea',
    statut: 'signe',
    destinataires: [
      { nom: 'Mehmet AZAD', email: 'm.azad@example.com', statut: 'signe', signe_le: '2026-05-22T10:14:00Z' },
      { nom: 'Sonia AZAD',  email: 's.azad@example.com', statut: 'signe', signe_le: '2026-05-22T10:35:00Z' },
    ],
    envoye_le: '2026-05-21T14:00:00Z',
    expire_le: '2026-06-04T14:00:00Z',
    signe_le:  '2026-05-22T10:35:00Z',
  },
  {
    id: 'sig_002',
    reference: 'SIG-2026-002',
    dossier_ref: 'AT-recNLOhp',
    societe_nom: 'MY FIRST ASSURANCE',
    document_nom: 'STATUTS MAJ.pdf',
    type: 'ses',
    statut: 'partiel',
    destinataires: [
      { nom: 'Karim BENOUDA', email: 'k.benouda@example.com', statut: 'signe',   signe_le: '2026-05-24T09:12:00Z' },
      { nom: 'Lina MARTINEZ', email: 'l.martinez@example.com', statut: 'attente', signe_le: null },
    ],
    envoye_le: '2026-05-23T16:30:00Z',
    expire_le: '2026-06-06T16:30:00Z',
    signe_le: null,
  },
  {
    id: 'sig_003',
    reference: 'SIG-2026-003',
    dossier_ref: 'AT-rec8347Lp',
    societe_nom: 'HARMONIE - RENOVATION',
    document_nom: 'PV de dissolution.pdf',
    type: 'ses',
    statut: 'envoye',
    destinataires: [
      { nom: 'Joël MARTIN', email: 'j.martin@example.com', statut: 'attente', signe_le: null },
    ],
    envoye_le: '2026-05-25T11:00:00Z',
    expire_le: '2026-06-08T11:00:00Z',
    signe_le: null,
  },
];

export default function Signatures() {
  const navigate = useNavigate();
  const [envois, setEnvois] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('tous');
  const [showNew, setShowNew] = useState(false);
  const [dossiers, setDossiers] = useState([]);

  // Form nouveau envoi
  const [newDossierId, setNewDossierId] = useState('');
  const [newDocName, setNewDocName] = useState('');
  const [newType, setNewType] = useState('ses');
  const [newDest, setNewDest] = useState([{ nom: '', email: '' }]);

  useEffect(() => {
    // Mock : à remplacer par signaturesAPI.list() une fois le backend prêt
    setTimeout(() => {
      setEnvois(MOCK_ENVOIS);
      setLoading(false);
    }, 200);

    // Liste de dossiers pour le formulaire de création
    dossiersAPI.list({ limit: 100 }).then(r => setDossiers(r.data || [])).catch(() => {});
  }, []);

  const counts = {
    tous:      envois.length,
    envoye:    envois.filter(e => e.statut === 'envoye').length,
    partiel:   envois.filter(e => e.statut === 'partiel').length,
    signe:     envois.filter(e => e.statut === 'signe').length,
    expire:    envois.filter(e => e.statut === 'expire' || e.statut === 'refuse').length,
  };

  const filtered = filter === 'tous'
    ? envois
    : filter === 'expire'
      ? envois.filter(e => e.statut === 'expire' || e.statut === 'refuse')
      : envois.filter(e => e.statut === filter);

  const totalSignedThisMonth = envois.filter(e => {
    if (e.statut !== 'signe' || !e.signe_le) return false;
    const d = new Date(e.signe_le);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  const delaiMoyenH = (() => {
    const completed = envois.filter(e => e.statut === 'signe' && e.signe_le && e.envoye_le);
    if (completed.length === 0) return 0;
    const total = completed.reduce((acc, e) => acc + (new Date(e.signe_le) - new Date(e.envoye_le)), 0);
    return Math.round(total / completed.length / 3600000);
  })();

  // ─── Création d'un envoi (mock — sera POST /api/signatures) ───
  const handleCreate = () => {
    if (!newDossierId || !newDocName || newDest.some(d => !d.nom || !d.email)) {
      alert('Remplissez tous les champs : dossier, document, et au moins un destinataire complet.');
      return;
    }
    const dossier = dossiers.find(d => d.id === newDossierId);
    const newSig = {
      id: `sig_${Date.now()}`,
      reference: `SIG-2026-${String(envois.length + 4).padStart(3, '0')}`,
      dossier_ref: dossier?.reference || newDossierId,
      societe_nom: dossier?.societe_nom || '—',
      document_nom: newDocName,
      type: newType,
      statut: 'envoye',
      destinataires: newDest.map(d => ({ ...d, statut: 'attente', signe_le: null })),
      envoye_le: new Date().toISOString(),
      expire_le: new Date(Date.now() + 14 * 86400000).toISOString(),
      signe_le: null,
    };
    setEnvois([newSig, ...envois]);
    setShowNew(false);
    setNewDossierId(''); setNewDocName(''); setNewType('ses');
    setNewDest([{ nom: '', email: '' }]);
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* ═══════════════════════════════════════════════════════════════════
          BANDEAU "APERÇU NON ACTIF" — visible en haut, sticky.
          Tant que le backend Lexback Sign n'est pas branché, on grise toute
          la page (overlay + opacity + pointer-events:none) pour qu'il soit
          évident que rien n'est fonctionnel et qu'il s'agit d'une maquette
          de présentation interne.
          ═══════════════════════════════════════════════════════════════════ */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'linear-gradient(90deg, rgba(184,134,11,0.95) 0%, rgba(176,48,32,0.92) 100%)',
        color: '#fff',
        padding: '10px 18px',
        borderRadius: '8px',
        marginBottom: '14px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        fontFamily: 'DM Sans, sans-serif',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '16px' }}>🚧</span>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600 }}>
              Aperçu — fonctionnalité en cours de développement
            </div>
            <div style={{ fontSize: '11.5px', opacity: 0.92, marginTop: '1px' }}>
              Cette page présente la maquette de Lexback Sign. Les boutons sont désactivés et les données affichées sont fictives.
            </div>
          </div>
        </div>
        <span style={{
          fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em',
          background: 'rgba(255,255,255,0.18)', padding: '4px 10px', borderRadius: '999px',
          border: '0.5px solid rgba(255,255,255,0.35)',
          whiteSpace: 'nowrap',
        }}>
          Pas encore actif
        </span>
      </div>

      {/* Contenu grisé + désactivé tant que le backend n'est pas branché */}
      <div style={{
        opacity: 0.55,
        filter: 'grayscale(0.65)',
        pointerEvents: 'none',
        userSelect: 'none',
      }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1A1916', display: 'flex', alignItems: 'center', gap: '10px' }}>
            Lexback Sign
            <span style={{ fontSize: '10px', fontWeight: 500, color: '#B8860B', background: 'rgba(184,134,11,0.10)', padding: '2px 8px', borderRadius: '999px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Bêta</span>
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(26,25,22,0.4)', marginTop: '2px' }}>Signature électronique souveraine — eIDAS, hébergement EU</div>
        </div>
        <button
          onClick={() => setShowNew(true)}
          style={{ background: '#B8860B', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '7px', fontSize: '12px', fontWeight: 500, cursor: 'not-allowed' }}
        >
          + Nouvel envoi
        </button>
      </div>

      {/* Pitch — pourquoi Lexback Sign vs Yousign */}
      <div style={{ background: 'linear-gradient(135deg, rgba(184,134,11,0.06) 0%, rgba(26,85,160,0.04) 100%)', border: '0.5px solid rgba(184,134,11,0.18)', borderRadius: '10px', padding: '14px 18px', marginBottom: '1.25rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px' }}>
          <div>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#B8860B', fontWeight: 600, marginBottom: '4px' }}>🇫🇷 Souverain</div>
            <div style={{ fontSize: '11.5px', color: 'rgba(26,25,22,0.7)', lineHeight: 1.4 }}>Hébergement Cloudflare EU, conforme RGPD, horodatage qualifié</div>
          </div>
          <div>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#B8860B', fontWeight: 600, marginBottom: '4px' }}>⚖️ eIDAS</div>
            <div style={{ fontSize: '11.5px', color: 'rgba(26,25,22,0.7)', lineHeight: 1.4 }}>SES (signature simple) et SEA (avancée, identité vérifiée)</div>
          </div>
          <div>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#B8860B', fontWeight: 600, marginBottom: '4px' }}>🔗 Intégré</div>
            <div style={{ fontSize: '11.5px', color: 'rgba(26,25,22,0.7)', lineHeight: 1.4 }}>Workflow natif Lexback, statut auto-synchro avec le dossier</div>
          </div>
          <div>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#B8860B', fontWeight: 600, marginBottom: '4px' }}>💸 0 coût/envoi</div>
            <div style={{ fontSize: '11.5px', color: 'rgba(26,25,22,0.7)', lineHeight: 1.4 }}>Pas de pricing à l'envoi, inclus dans votre abonnement Lexback</div>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '1.25rem' }}>
        <div style={{ ...card, padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '4px' }}>En cours</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '24px', fontWeight: 700, color: '#1A55A0' }}>
            {counts.envoye + counts.partiel}
          </div>
        </div>
        <div style={{ ...card, padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '4px' }}>Signés ce mois</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '24px', fontWeight: 700, color: '#1A7A4A' }}>{totalSignedThisMonth}</div>
        </div>
        <div style={{ ...card, padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '4px' }}>Délai moyen</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '24px', fontWeight: 700, color: '#1A1916' }}>{delaiMoyenH}h</div>
        </div>
        <div style={{ ...card, padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '4px' }}>Expirés / refusés</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '24px', fontWeight: 700, color: counts.expire > 0 ? '#B03020' : '#1A1916' }}>{counts.expire}</div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '1rem' }}>
        {[
          { key: 'tous',    label: 'Tous',         count: counts.tous },
          { key: 'envoye',  label: 'Envoyés',      count: counts.envoye },
          { key: 'partiel', label: 'Partiels',     count: counts.partiel },
          { key: 'signe',   label: 'Signés',       count: counts.signe },
          { key: 'expire',  label: 'Expirés',      count: counts.expire },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} style={{
            padding: '4px 12px', borderRadius: '999px', fontSize: '11px', cursor: 'pointer',
            border: '0.5px solid rgba(0,0,0,0.1)', fontFamily: 'DM Sans, sans-serif',
            background: filter === f.key ? '#1A1916' : 'transparent',
            color:      filter === f.key ? '#fff'    : 'rgba(26,25,22,0.5)',
          }}>{f.label} · {f.count}</button>
        ))}
      </div>

      {/* Table */}
      <div style={{ ...card, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'rgba(26,25,22,0.4)', fontSize: '13px' }}>Chargement…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'rgba(26,25,22,0.35)', fontSize: '13px' }}>
            Aucun envoi{filter !== 'tous' ? ' dans ce statut' : ''}.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '0.5px solid rgba(0,0,0,0.07)' }}>
                {['Référence', 'Société', 'Document', 'Type', 'Destinataires', 'Statut', 'Envoyé le', 'Expire', 'Actions'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, i) => {
                const totalDest = e.destinataires.length;
                const signedDest = e.destinataires.filter(d => d.statut === 'signe').length;
                return (
                  <tr key={e.id} style={{ borderBottom: i < filtered.length - 1 ? '0.5px solid rgba(0,0,0,0.05)' : 'none' }}
                    onMouseEnter={ev => ev.currentTarget.style.background = '#F5F3EE'}
                    onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '11px 14px', fontSize: '12px', fontWeight: 500, color: '#1A1916', fontFamily: 'monospace' }}>{e.reference}</td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: 'rgba(26,25,22,0.7)' }}>
                      <div>{e.societe_nom}</div>
                      <div style={{ fontSize: '10.5px', color: 'rgba(26,25,22,0.4)', fontFamily: 'monospace' }}>{e.dossier_ref}</div>
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: '12px', color: 'rgba(26,25,22,0.7)', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.document_nom}>
                      📄 {e.document_nom}
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: '11px', color: 'rgba(26,25,22,0.55)' }} title={TYPE_SIG[e.type].tooltip}>
                      {TYPE_SIG[e.type].label}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontSize: '11.5px', color: 'rgba(26,25,22,0.7)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500, color: signedDest === totalDest ? '#1A7A4A' : '#1A1916' }}>
                          {signedDest}/{totalDest}
                        </span>
                        <span style={{ color: 'rgba(26,25,22,0.4)' }}>
                          {e.destinataires.map(d => d.nom).join(', ')}
                        </span>
                      </div>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <Badge statut={e.statut} />
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: '11px', color: 'rgba(26,25,22,0.5)', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDate(e.envoye_le)}
                    </td>
                    <td style={{ padding: '11px 14px', fontSize: '11px', color: 'rgba(26,25,22,0.45)', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtDate(e.expire_le)}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button title="Voir détail" style={{ background: 'transparent', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '5px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', color: 'rgba(26,25,22,0.6)' }}>
                          Voir
                        </button>
                        {e.statut !== 'signe' && (
                          <button title="Relancer" style={{ background: 'transparent', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '5px', padding: '3px 8px', fontSize: '11px', cursor: 'pointer', color: '#B8860B' }}>
                            Relancer
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modale création — drawer right side */}
      </div>{/* /Contenu grisé */}
      {showNew && (
        <>
          <div onClick={() => setShowNew(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 200 }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(520px, 95vw)', background: '#fff', boxShadow: '-12px 0 32px rgba(0,0,0,0.12)', zIndex: 201, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '18px 24px', borderBottom: '0.5px solid #e5e1d8', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '17px', fontWeight: 600, color: '#1A1916' }}>Nouvel envoi de signature</div>
              <button onClick={() => setShowNew(false)} style={{ background: 'transparent', border: '1px solid #e5e1d8', borderRadius: '8px', cursor: 'pointer', fontSize: '18px', color: '#9a8f7a', width: '32px', height: '32px' }}>×</button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              {/* Dossier */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: '#8b8576', marginBottom: '6px', letterSpacing: '0.05em' }}>Dossier rattaché</div>
                <select
                  value={newDossierId}
                  onChange={e => setNewDossierId(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e5e1d8', fontSize: '13px', background: '#fff', color: '#1A1916' }}
                >
                  <option value="">— Choisir un dossier —</option>
                  {dossiers.map(d => (
                    <option key={d.id} value={d.id}>{d.societe_nom || d.reference} · {d.reference}</option>
                  ))}
                </select>
              </div>

              {/* Document */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: '#8b8576', marginBottom: '6px', letterSpacing: '0.05em' }}>Document à signer</div>
                <input
                  type="text"
                  value={newDocName}
                  onChange={e => setNewDocName(e.target.value)}
                  placeholder="Ex : PV AGE - cession parts.pdf"
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e5e1d8', fontSize: '13px', background: '#fff', color: '#1A1916' }}
                />
                <div style={{ fontSize: '10.5px', color: '#9a8f7a', marginTop: '4px' }}>
                  Le document sera prélevé automatiquement depuis les documents juridiques du dossier (intégration en cours).
                </div>
              </div>

              {/* Type signature */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: '#8b8576', marginBottom: '6px', letterSpacing: '0.05em' }}>Niveau de signature</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {Object.entries(TYPE_SIG).map(([key, t]) => (
                    <button
                      key={key}
                      onClick={() => setNewType(key)}
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: '7px',
                        border: newType === key ? '1.5px solid #B8860B' : '1px solid #e5e1d8',
                        background: newType === key ? 'rgba(184,134,11,0.06)' : '#fff',
                        cursor: 'pointer', textAlign: 'left',
                        fontFamily: 'inherit',
                      }}
                    >
                      <div style={{ fontSize: '12.5px', fontWeight: 600, color: '#1A1916' }}>{t.label}</div>
                      <div style={{ fontSize: '10.5px', color: '#9a8f7a', marginTop: '2px', lineHeight: 1.3 }}>{t.tooltip}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Destinataires */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: '#8b8576', letterSpacing: '0.05em' }}>Destinataires</div>
                  <button
                    onClick={() => setNewDest([...newDest, { nom: '', email: '' }])}
                    style={{ background: 'transparent', border: 'none', color: '#B8860B', cursor: 'pointer', fontSize: '11.5px', fontWeight: 500 }}
                  >
                    + Ajouter
                  </button>
                </div>
                {newDest.map((d, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 32px', gap: '6px', marginBottom: '6px' }}>
                    <input
                      type="text"
                      value={d.nom}
                      onChange={e => {
                        const copy = [...newDest]; copy[idx] = { ...copy[idx], nom: e.target.value }; setNewDest(copy);
                      }}
                      placeholder="Nom prénom"
                      style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #e5e1d8', fontSize: '12.5px' }}
                    />
                    <input
                      type="email"
                      value={d.email}
                      onChange={e => {
                        const copy = [...newDest]; copy[idx] = { ...copy[idx], email: e.target.value }; setNewDest(copy);
                      }}
                      placeholder="email@exemple.com"
                      style={{ padding: '7px 10px', borderRadius: '6px', border: '1px solid #e5e1d8', fontSize: '12.5px' }}
                    />
                    {newDest.length > 1 && (
                      <button
                        onClick={() => setNewDest(newDest.filter((_, i) => i !== idx))}
                        style={{ background: 'transparent', border: '1px solid #e5e1d8', borderRadius: '6px', cursor: 'pointer', color: '#B03020' }}
                      >×</button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 24px', borderTop: '0.5px solid #e5e1d8', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: '#fafaf8' }}>
              <button
                onClick={() => setShowNew(false)}
                style={{ background: 'transparent', border: '1px solid #e5e1d8', color: '#5C6B7A', padding: '8px 16px', borderRadius: '7px', fontSize: '12.5px', cursor: 'pointer' }}
              >
                Annuler
              </button>
              <button
                onClick={handleCreate}
                style={{ background: '#B8860B', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 500, cursor: 'pointer' }}
              >
                Envoyer pour signature
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
